import usePageTitle from "@/lib/usePageTitle";
import { IMAGES } from "@/lib/content";
import { useContent } from "@/lib/content-context";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function About() {
  usePageTitle("About");
  const { content } = useContent();
  const a = content.about;

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="overline mb-6">{a.hero_overline}</div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
              {a.hero_line1}<br/>{a.hero_line2}
            </h1>
            <p className="mt-8 text-[#5C5A56] text-lg leading-relaxed max-w-2xl">{a.hero_paragraph}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-square bg-cover bg-center" style={{ backgroundImage: `url('${IMAGES.about}')` }} />
          </div>
        </div>
      </section>

      {/* COMPANY FACTS */}
      <section className="bg-[#EAE6DF] py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1A1A1A]/10 border border-[#1A1A1A]/10">
          {(a.facts || []).map((f, i) => (
            <div key={i} className="bg-[#F8F7F4] p-8">
              <div className="overline mb-3">{f.key}</div>
              <div className={`text-[#1A1A1A] ${f.mono ? "font-mono-ed text-sm" : "font-serif-display text-2xl"}`}>{f.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline mb-5">{a.mission_overline}</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">{a.mission_heading}</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 text-[#5C5A56] text-lg leading-relaxed space-y-5">
            <p>{a.mission_paragraph1}</p>
            <p>{a.mission_paragraph2}</p>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-[#1A1A1A] text-[#F8F7F4] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5 text-[#C25934]">{a.timeline_overline}</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mb-16">{a.timeline_heading}</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/15 border border-white/15">
            {(a.timeline || []).map((t, i) => (
              <div key={i} data-testid={`timeline-${i}`} className="bg-[#1A1A1A] p-8">
                <div className="font-mono-ed text-[#C25934] text-sm">{t.year}</div>
                <div className="font-serif-display text-2xl mt-4">{t.title}</div>
                <div className="text-sm text-[#D9D4CC] mt-3 leading-relaxed">{t.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <h3 className="font-serif-display text-3xl md:text-5xl max-w-2xl">{a.cta_heading}</h3>
          <Link to="/contact" data-testid="about-cta" className="btn-primary">
            {a.cta_button} <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
